import { c as _c } from "react/compiler-runtime";
import { getVendorStoriesByVendorId } from "@/services/api/vendor/stories";
import ImageComponent from "@/components/ui/image";
import { useQuery } from "@tanstack/react-query";
import { memo, useEffect, useMemo, useState } from "react";

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const OurStorySkeleton = memo(() => {
  const $ = _c(9);
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="w-36 h-1 bg-[#037A6A]/20 rounded-full" />;
    t1 = <div className="w-48 h-8 bg-[#037A6A]/15 rounded-lg" />;
    t2 = <div className="w-2/3 h-4 bg-gray-200 rounded" />;
    t3 = <div className="w-1/2 h-4 bg-gray-200 rounded" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="hidden md:block px-8 py-14"><div className="flex flex-col items-center gap-6">{t0}{t1}{t2}{t3}<div className="flex gap-4 mt-4 w-full justify-center">{[...Array(3)].map(_temp)}</div></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="w-24 h-6 bg-[#037A6A]/15 rounded" />;
    t6 = <div className="w-full h-4 bg-gray-200 rounded" />;
    t7 = <div className="w-3/4 h-4 bg-gray-200 rounded" />;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t5 = $[5];
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="w-full bg-[#F7FFFE] animate-pulse">{t4}<div className="md:hidden px-4 py-8 flex flex-col items-center gap-4">{t5}{t6}{t7}<div className="grid grid-cols-2 gap-3 w-full mt-2">{[...Array(2)].map(_temp2)}</div></div></div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  return t8;
});
OurStorySkeleton.displayName = "OurStorySkeleton";

// ─── Section heading ──────────────────────────────────────────────────────────
const SectionHeading = t0 => {
  const $ = _c(9);
  const {
    mobile: t1
  } = t0;
  const mobile = t1 === undefined ? false : t1;
  const t2 = `flex flex-col items-center ${mobile ? "gap-1.5" : "gap-2"}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="h-px w-12 bg-gradient-to-r from-transparent to-[#037A6A]" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = `font-outfit font-semibold uppercase tracking-[0.2em] text-[#037A6A] ${mobile ? "text-[10px]" : "text-xs"}`;
  let t5;
  if ($[1] !== t4) {
    t5 = <span className={t4}>Our Story</span>;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="h-px w-12 bg-gradient-to-l from-transparent to-[#037A6A]" />;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== t5) {
    t7 = <div className="flex items-center gap-3">{t3}{t5}{t6}</div>;
    $[4] = t5;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== t2 || $[7] !== t7) {
    t8 = <div className={t2}>{t7}</div>;
    $[6] = t2;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  return t8;
};
interface OurStoryProps {
  vendorId: string;
  initialStory?: Record<string, unknown> | null;
}

// ─── Main OurStory ────────────────────────────────────────────────────────────
const OurStory = memo(t0 => {
  const $ = _c(34);
  const {
    vendorId,
    initialStory
  } = t0;
  const [currentIndex, setCurrentIndex] = useState(0);
  let t1;
  let t2;
  if ($[0] !== vendorId) {
    t1 = ["storyData", vendorId];
    t2 = () => getVendorStoriesByVendorId(vendorId as string);
    $[0] = vendorId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = !!vendorId;
  let t4;
  if ($[3] !== initialStory || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      initialData: initialStory,
      staleTime: Infinity,
      select: _temp3
    };
    $[3] = initialStory;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: storyData,
    isLoading,
    isError
  } = useQuery(t4);
  let t5;
  if ($[8] !== storyData?.media_urls) {
    t5 = storyData?.media_urls || [];
    $[8] = storyData?.media_urls;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const mediaUrls = t5;
  let t6;
  let t7;
  if ($[10] !== mediaUrls.length) {
    t6 = () => {
      if (mediaUrls.length <= 1) {
        return;
      }
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % mediaUrls.length);
      }, 5000);
      return () => clearInterval(interval);
    };
    t7 = [mediaUrls.length];
    $[10] = mediaUrls.length;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  const hasContent = storyData?.text?.trim() || storyData?.media_urls && storyData.media_urls.length > 0;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SectionHeading />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== storyData) {
    t9 = storyData?.text && <p className="font-outfit text-lg font-light text-[#4a4a4a] text-center leading-[1.85] max-w-3xl">{storyData.text}</p>;
    $[14] = storyData;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== mediaUrls) {
    t10 = mediaUrls.length > 0 && <div className={`w-full grid gap-4 mt-2 ${mediaUrls.length === 1 ? "grid-cols-1 max-w-sm mx-auto" : mediaUrls.length === 2 ? "grid-cols-2" : mediaUrls.length >= 4 ? "grid-cols-2 xl:grid-cols-4" : "grid-cols-2 xl:grid-cols-3"}`}>{mediaUrls.map(_temp4)}</div>;
    $[16] = mediaUrls;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <div className="hidden lg:block w-full bg-gradient-to-b from-[#F0FEFA] to-white border-y border-[#037A6A]/10"><div className="px-8 py-14 max-w-5xl mx-auto flex flex-col items-center gap-8">{t8}{t9}{t10}</div></div>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const desktopView = t11;
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SectionHeading mobile={true} />;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== storyData) {
    t13 = storyData?.text && <p className="font-outfit text-sm md:text-base font-light text-[#4a4a4a] text-center leading-[1.85] px-2">{storyData.text}</p>;
    $[22] = storyData;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== currentIndex || $[25] !== mediaUrls) {
    t14 = mediaUrls.length > 0 && <div className="w-full flex flex-col items-center"><div className="relative w-full aspect-square max-w-md rounded-2xl overflow-hidden shadow-lg border border-[#037A6A]/10 bg-white transition-all duration-500"><div key={currentIndex} className="w-full h-full transition-all duration-700 ease-in-out"><ImageComponent src={mediaUrls[currentIndex]} alt={`story image ${currentIndex + 1}`} className="w-full h-full object-cover" width={500} height={500} quality={80} sizes="(max-width: 768px) 100vw, 500px" /></div></div>{mediaUrls.length > 1 && <div className="flex justify-center mt-6 space-x-3">{mediaUrls.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`transition-all duration-500 rounded-full h-1.5 ${index === currentIndex ? "w-8 bg-[#037A6A]" : "w-2 bg-[#037A6A]/20"}`} aria-label={`Go to story image ${index + 1}`} />)}</div>}</div>;
    $[24] = currentIndex;
    $[25] = mediaUrls;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t13 || $[28] !== t14) {
    t15 = <div className="lg:hidden w-full bg-gradient-to-b from-[#F0FEFA] to-white border-t border-[#037A6A]/10"><div className="px-4 py-8 max-w-2xl mx-auto flex flex-col items-center gap-6">{t12}{t13}{t14}</div></div>;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const mobileView = t15;
  if (isLoading) {
    let t16;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <OurStorySkeleton />;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    return t16;
  }
  if (isError || !isLoading && !hasContent) {
    return null;
  }
  let t16;
  if ($[31] !== desktopView || $[32] !== mobileView) {
    t16 = <>{desktopView}{mobileView}</>;
    $[31] = desktopView;
    $[32] = mobileView;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
});
OurStory.displayName = "OurStory";
export default OurStory;
function _temp(_, i) {
  return <div key={i} className="w-56 h-44 bg-gray-200 rounded-xl flex-1" />;
}
function _temp2(__0, i_0) {
  return <div key={i_0} className="w-full aspect-video bg-gray-200 rounded-xl" />;
}
function _temp3(data) {
  if (!data) {
    return null;
  }
  const res = data as {
    results?: Array<{
      media_urls?: string[];
      story?: string;
    }>;
  };
  if (Array.isArray(res.results)) {
    return res.results.length > 0 ? res.results[0] : null;
  }
  return data as {
    media_urls?: string[];
    story?: string;
  };
}
function _temp4(url, i) {
  return <div key={url} className="aspect-square rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 border border-[#037A6A]/10"><ImageComponent src={url} alt={`story image ${i + 1}`} className="w-full h-full object-cover" width={400} height={400} quality={80} sizes="(max-width: 1024px) 100vw, 25vw" /></div>;
}